// Approval chains (D12) — staff visibility of a SENT item's chain: the chain
// status line as the headline, the live version's stage rows in order (party,
// role, status, when, return comment), earlier versions collapsed under
// "Earlier versions (void)", the D9 attestations with name and time, and the
// staff actions — each behind a confirmation whose text says exactly what the
// action does. Status words are the server's labels.
//
// Renders nothing for an item without a chain (the read 404s → null).

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, PenLine, RotateCcw, Send, XCircle } from "lucide-react";
import {
  Badge,
  Button,
  FormField,
  SubTable,
  Textarea,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  approvalChainsApi,
  type ApprovalChainDto,
  type ApprovalChainStageDto,
  type ReviseApprovalChainRequest,
} from "@/api/approvalChains";
import { collectionTemplatesApi } from "@/api/collectionTemplates";
import {
  approvalChainKeys,
  invalidateApprovalChainReads,
} from "@/hooks/collection/approvalChainKeys";
import { useDateFormatter } from "@/utils/datetime";
import {
  APPROVAL_CHAIN_STAGE_TONE,
  APPROVAL_CHAIN_STATUS_TONE,
} from "@/components/Collection/collectionStatusLabels";
import {
  SenderDetailsSection,
  templateHasSenderFields,
  type SenderValues,
} from "@/components/Collection/SenderDetailsSection";

export const CANCEL_CHAIN_MESSAGE =
  "Cancelling stops this document's routing. Every party loses access to it. To change the document, send it again.";
export const REVISE_CHAIN_MESSAGE =
  "This creates a new version and restarts routing from the first stage. Earlier signatures stay in history and are not carried onto the new version.";

export interface ApprovalChainTimelineProps {
  itemId: string;
  /** The host's permission check (staff auth). The actions shown follow it;
   *  the server re-checks every one. */
  hasPermission: (permission: string) => boolean;
  /** The item's package — the replaced-file upload is a package route. */
  packageId: string;
  /** What "Revise and restart" needs to offer the sender-details editor. */
  revise?: {
    templateId: string;
    dealId: string | null;
    recipientName?: string | null;
    /** The returned version's sender values, when the host has them. */
    savedFormDataJson?: string | null;
    staffName?: string | null;
    staffEmail?: string | null;
  };
}

/** The Phoenix countersign is due: the Note Administrator stage is the active one. */
export function phoenixStageActive(chain: ApprovalChainDto): boolean {
  return (
    chain.status === "Active" &&
    chain.stages.some((s) => s.role === "Phoenix" && s.status === "Active")
  );
}

function parseSaved(json: string | null | undefined): SenderValues | undefined {
  if (!json || !json.trim()) return undefined;
  try {
    const parsed = JSON.parse(json) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as SenderValues) : undefined;
  } catch {
    return undefined;
  }
}

function StageTable({ rows, caption }: { rows: ApprovalChainStageDto[]; caption: string }) {
  const { dateTime } = useDateFormatter();
  const columns: SubTableColumn<ApprovalChainStageDto>[] = [
    { key: "order", header: "#", render: (s) => s.order },
    {
      key: "party",
      header: "Party",
      render: (s) => (
        <span className="flex flex-col">
          <span>{s.partyName}</span>
          {s.partyCompany && <span className="text-caption text-gray-mid">{s.partyCompany}</span>}
        </span>
      ),
    },
    { key: "role", header: "Role", render: (s) => s.roleLabel },
    {
      key: "status",
      header: "Status",
      render: (s) => <Badge status={APPROVAL_CHAIN_STAGE_TONE[s.status] ?? "draft"}>{s.statusLabel}</Badge>,
    },
    {
      key: "when",
      header: "When",
      render: (s) => {
        const at = s.completedAt ?? s.returnedAt ?? s.activatedAt;
        return at ? dateTime(at) : "—";
      },
    },
    {
      key: "comment",
      header: "Return comment",
      render: (s) => (s.returnComment ? <span className="whitespace-pre-wrap">{s.returnComment}</span> : "—"),
    },
  ];
  return (
    <SubTable
      columns={columns}
      rows={rows}
      rowKey={(s) => s.id}
      caption={caption}
      chromeThreshold={Number.POSITIVE_INFINITY}
      rowTestId={(s) => `chain-stage-row-${s.documentVersion}-${s.order}-${s.role}`}
      empty={{
        message: "No stage has been routed yet.",
        door: { kind: "derived", explain: "Stages appear when the package is sent." },
      }}
    />
  );
}

export function ApprovalChainTimeline({
  itemId,
  packageId,
  hasPermission,
  revise,
}: ApprovalChainTimelineProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const { dateTime } = useDateFormatter();
  const canSend = hasPermission("Collection.SendPackages");
  const canManage = hasPermission("Collection.ManagePackages");
  const canCountersignPerm = hasPermission("Collection.Countersign");

  const [dialog, setDialog] = useState<"resend" | "cancel" | "revise" | "countersign" | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [reviseNote, setReviseNote] = useState("");
  const [reviseFile, setReviseFile] = useState<File | null>(null);
  const [reviseValuesJson, setReviseValuesJson] = useState<string | null>(null);
  const [reviseTouched, setReviseTouched] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const chainQuery = useQuery({
    queryKey: approvalChainKeys.item(itemId),
    queryFn: () => approvalChainsApi.getForItem(itemId),
  });
  const chain = chainQuery.data;

  const templateQuery = useQuery({
    queryKey: approvalChainKeys.template(revise?.templateId ?? ""),
    queryFn: () => collectionTemplatesApi.get(revise!.templateId),
    enabled: dialog === "revise" && Boolean(revise?.templateId),
  });

  const closeDialog = () => {
    setDialog(null);
    setCancelReason("");
    setReviseNote("");
    setReviseFile(null);
    setReviseValuesJson(null);
    setReviseTouched(false);
  };

  // Each mutation's onSuccess also invalidates the chain family (inline, so
  // the stale-after-save gate sees it in the options block).
  const onDone = (message: string) => (dto: ApprovalChainDto) => {
    qc.setQueryData(approvalChainKeys.item(itemId), dto);
    toast.success(message);
    closeDialog();
  };

  const resendMut = useMutation({
    mutationFn: () => approvalChainsApi.resendNotification(itemId),
    onSuccess: (dto) => {
      onDone("Notification sent again.")(dto);
      invalidateApprovalChainReads(qc);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });
  const cancelMut = useMutation({
    mutationFn: (reason: string) => approvalChainsApi.cancel(itemId, reason),
    onSuccess: (dto) => {
      onDone("Routing cancelled.")(dto);
      invalidateApprovalChainReads(qc);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });
  const reviseMut = useMutation({
    mutationFn: async (input: { file: File | null; body: ReviseApprovalChainRequest }) => {
      if (input.file) await approvalChainsApi.upload(packageId, itemId, input.file);
      return approvalChainsApi.revise(itemId, input.body);
    },
    onSuccess: (dto) => {
      onDone("New version created — routing restarted from the first stage.")(dto);
      invalidateApprovalChainReads(qc);
    },
    onError: (e) => {
      toast.error(extractErrorMessage(e));
      // A replaced file may have landed before revise failed.
      invalidateApprovalChainReads(qc);
    },
  });
  const countersignMut = useMutation({
    mutationFn: () => approvalChainsApi.countersign(itemId),
    onSuccess: (dto) => {
      onDone("Countersigned.")(dto);
      invalidateApprovalChainReads(qc);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  if (!chain) return null;

  const activeStage = chain.stages.find((s) => s.status === "Active");
  const showResend = canSend && chain.canResendNotification;
  const showCancel = canSend && chain.canCancel;
  const showRevise = canManage && chain.status === "Returned" && chain.canRevise;
  const showCountersign = canCountersignPerm && phoenixStageActive(chain);
  const showDownload = chain.status !== "Draft";

  const historyByVersion = new Map<number, ApprovalChainStageDto[]>();
  for (const s of chain.history) {
    const arr = historyByVersion.get(s.documentVersion) ?? [];
    arr.push(s);
    historyByVersion.set(s.documentVersion, arr);
  }
  const attestations = [chain.engagementAttestation, chain.clearanceAttestation].filter(
    (a): a is NonNullable<typeof a> => a !== null,
  );
  const reviseTemplate = templateQuery.data;
  const reviseHasSenderFields =
    reviseTemplate !== undefined && templateHasSenderFields(reviseTemplate.formSchemaJson);

  const download = async () => {
    setDownloading(true);
    try {
      const { url } = await approvalChainsApi.documentUrl(itemId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section
      className="flex flex-col gap-space-3 rounded-md border border-border-light bg-white-pure p-space-3"
      data-testid={`approval-chain-timeline-${itemId}`}
      aria-label="Approval chain"
    >
      <header className="flex flex-wrap items-center gap-space-2">
        <span className="text-small font-semibold text-onyx">Approval chain</span>
        <Badge status={APPROVAL_CHAIN_STATUS_TONE[chain.status] ?? "draft"} data-testid="chain-headline">
          {chain.statusLabel}
        </Badge>
        <span className="text-caption text-gray-mid">Version {chain.documentVersion}</span>
      </header>
      {chain.status === "Cancelled" && chain.cancelReason && (
        <p className="m-0 text-caption text-gray-mid">
          Cancelled{chain.cancelledAt ? ` ${dateTime(chain.cancelledAt)}` : ""}: {chain.cancelReason}
        </p>
      )}

      <StageTable rows={chain.stages} caption={`Approval chain stages, version ${chain.documentVersion}`} />

      {historyByVersion.size > 0 && (
        <details data-testid="chain-history">
          <summary className="cursor-pointer text-small text-onyx">Earlier versions (void)</summary>
          <div className="mt-space-2 flex flex-col gap-space-3">
            {[...historyByVersion.entries()].map(([version, rows]) => (
              <div key={version} className="flex flex-col gap-space-1">
                <span className="text-caption font-medium text-gray-mid">Version {version}</span>
                <StageTable
                  rows={[...rows].sort((a, b) => a.order - b.order)}
                  caption={`Approval chain stages, version ${version} (void)`}
                />
              </div>
            ))}
          </div>
        </details>
      )}

      {attestations.length > 0 && (
        <div className="flex flex-col gap-space-1" data-testid="chain-attestation-record">
          <span className="text-caption font-medium text-onyx">Staff attestations</span>
          <ul className="m-0 pl-space-5 text-caption text-onyx">
            {attestations.map((a) => (
              <li key={`${a.wording}-${a.at}`}>
                {a.wording}
                <span className="text-gray-mid">
                  {" "}
                  — {a.userName ?? "a staff user"}, {dateTime(a.at)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(showResend || showCancel || showRevise || showCountersign || showDownload) && (
        <div className="flex flex-wrap items-center gap-space-2">
          {showCountersign && (
            <Button type="button" size="sm" onClick={() => setDialog("countersign")}>
              <PenLine size={14} aria-hidden /> Countersign
            </Button>
          )}
          {showResend && (
            <Button type="button" size="sm" variant="secondary" onClick={() => setDialog("resend")}>
              <Send size={14} aria-hidden /> Resend notification
            </Button>
          )}
          {showRevise && (
            <Button type="button" size="sm" variant="secondary" onClick={() => setDialog("revise")}>
              <RotateCcw size={14} aria-hidden /> Revise and restart
            </Button>
          )}
          {showDownload && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void download()}
              loading={downloading}
              disabled={downloading}
            >
              <Download size={14} aria-hidden /> Download current PDF
            </Button>
          )}
          {showCancel && (
            <Button type="button" size="sm" variant="destructive" onClick={() => setDialog("cancel")}>
              <XCircle size={14} aria-hidden /> Cancel chain
            </Button>
          )}
        </div>
      )}

      <ConfirmModal
        open={dialog === "resend"}
        title="Resend notification"
        message={
          activeStage
            ? `This emails ${activeStage.partyName} (${activeStage.roleLabel}) the notification for their stage again. The document, its signatures and its routing do not change.`
            : "This emails the party whose turn it is the notification for their stage again. The document, its signatures and its routing do not change."
        }
        confirmLabel="Send it again"
        onConfirm={() => resendMut.mutate()}
        onCancel={closeDialog}
      />

      <ConfirmModal
        open={dialog === "countersign"}
        title="Countersign"
        message="This countersigns the document for Phoenix as Note Administrator, recorded under your name, and completes the Note Administrator stage."
        confirmLabel="Countersign now"
        onConfirm={() => countersignMut.mutate()}
        onCancel={closeDialog}
      />

      <Modal
        open={dialog === "cancel"}
        onClose={closeDialog}
        title="Cancel chain"
        size="sm"
        dismissOnBackdropClick={false}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={closeDialog}>
              Keep routing
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={!cancelReason.trim() || cancelMut.isPending}
              loading={cancelMut.isPending}
              onClick={() => cancelMut.mutate(cancelReason.trim())}
            >
              Cancel routing
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-space-3">
          <p className="m-0 text-small text-onyx">{CANCEL_CHAIN_MESSAGE}</p>
          <FormField label="Reason" required>
            <Textarea
              rows={3}
              maxLength={4000}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </FormField>
        </div>
      </Modal>

      <Modal
        open={dialog === "revise"}
        onClose={closeDialog}
        title="Revise and restart"
        size={reviseHasSenderFields ? "xl" : "md"}
        dismissOnBackdropClick={false}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={closeDialog}>
              Back
            </Button>
            <Button
              type="button"
              disabled={reviseMut.isPending}
              loading={reviseMut.isPending}
              onClick={() =>
                reviseMut.mutate({
                  file: reviseFile,
                  body: {
                    ...(reviseTouched && reviseValuesJson !== null
                      ? { savedFormDataJson: reviseValuesJson }
                      : {}),
                    ...(reviseNote.trim() ? { note: reviseNote.trim() } : {}),
                  },
                })
              }
            >
              Restart routing
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-space-4">
          <p className="m-0 text-small text-onyx">{REVISE_CHAIN_MESSAGE}</p>
          {reviseHasSenderFields && reviseTemplate && (
            <div
              className="flex flex-col gap-space-2"
              onChangeCapture={() => setReviseTouched(true)}
              onInputCapture={() => setReviseTouched(true)}
            >
              <p className="m-0 text-caption text-gray-mid">
                {revise?.savedFormDataJson
                  ? "Sender details as sent on the returned version. They are re-sent only if you change a field."
                  : "Sender details start from the deal's current data, not the returned version. They are re-sent only if you change a field."}
              </p>
              <SenderDetailsSection
                templateId={reviseTemplate.id}
                templateName={reviseTemplate.name}
                formSchemaJson={reviseTemplate.formSchemaJson}
                dealId={revise?.dealId ?? null}
                recipientName={revise?.recipientName ?? null}
                staffName={revise?.staffName ?? null}
                staffEmail={revise?.staffEmail ?? null}
                initialValues={parseSaved(revise?.savedFormDataJson)}
                onValuesChange={(_, json) => setReviseValuesJson(json)}
                hidePreview
              />
            </div>
          )}
          <FormField label="Replace the document (optional)" helperText="PDF or DOCX, up to 25 MB.">
            <input
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => setReviseFile(e.target.files?.[0] ?? null)}
              className="text-small"
            />
          </FormField>
          <FormField label="Note (optional)">
            <Textarea rows={2} value={reviseNote} onChange={(e) => setReviseNote(e.target.value)} />
          </FormField>
        </div>
      </Modal>
    </section>
  );
}
